import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle2, FileUp, Loader2, Paperclip, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { APPAREL_CATEGORIES, CATEGORIES, SITE } from "@/config/site";
import { API, track, whatsappLink } from "@/lib/site";
import { cn } from "@/lib/utils";
import { Link } from "react-router-dom";
import { WhatsAppIcon } from "./Primitives";

const MAX_FILE = 8 * 1024 * 1024;
const ALLOWED = [".jpg", ".jpeg", ".png", ".webp", ".pdf", ".ai", ".eps"];
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const PHONE_RE = /^\+?[0-9 ()-]{9,20}$/;

const CATEGORY_EVENTS = {
  "custom-clothing": "tshirt_enquiry",
  "uniforms-embroidery": "corporate_branding_enquiry",
  "printing-branding": "corporate_branding_enquiry",
  "corporate-gifts": "corporate_branding_enquiry",
  "xhosa-cultural": "cultural_product_enquiry",
  "bulk-group": "bulk_order_enquiry",
};

const Field = ({ id, label, required, error, hint, children, className }) => (
  <div className={cn("space-y-2", className)}>
    <Label htmlFor={id} className="text-sm font-semibold text-[#161616]">
      {label} {required ? <span className="text-black/45" aria-hidden="true">*</span> : <span className="font-normal text-black/45">(optional)</span>}
    </Label>
    {children}
    {hint && !error ? <p id={`${id}-hint`} className="text-xs text-black/55">{hint}</p> : null}
    {error ? <p id={`${id}-error`} role="alert" className="text-xs font-medium text-red-700" data-testid={`quote-form-${id}-error`}>{error}</p> : null}
  </div>
);

export default function QuoteForm({ variant = "quote", defaultCategory = "", defaultProduct = "", sourcePage, heading, className }) {
  const isBulk = variant === "bulk";
  const initial = useMemo(() => ({
    first_name: "", surname: "", email: "", phone: "", company: "",
    product_category: CATEGORIES.some((c) => c.value === defaultCategory) ? defaultCategory : (isBulk ? "bulk-group" : ""),
    product: defaultProduct || "", quantity: "", sizes: "", colours: "",
    design_requirements: "", message: "", preferred_contact: "whatsapp",
    required_date: "", delivery_location: "", budget: "", consent: false, website: "",
  }), [defaultCategory, defaultProduct, isBulk]);

  const [v, setV] = useState(initial);
  const [file, setFile] = useState(null);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle");
  const [result, setResult] = useState(null);
  const startedAt = useRef(Date.now());
  const formRef = useRef(null);
  const fileRef = useRef(null);

  useEffect(() => { setV(initial); }, [initial]);

  const set = (k) => (e) => {
    const val = e && e.target ? e.target.value : e;
    setV((p) => ({ ...p, [k]: val }));
    if (errors[k]) setErrors((p) => ({ ...p, [k]: undefined }));
  };

  const showApparel = APPAREL_CATEGORIES.includes(v.product_category);
  const today = new Date().toISOString().slice(0, 10);

  const validate = () => {
    const e = {};
    if (!v.first_name.trim()) e.first_name = "Please enter your first name";
    if (!v.surname.trim()) e.surname = "Please enter your surname";
    if (!EMAIL_RE.test(v.email.trim())) e.email = "Please enter a valid email address";
    if (!PHONE_RE.test(v.phone.trim()) || v.phone.replace(/\D/g, "").length < 9) e.phone = "Please enter a valid phone / WhatsApp number";
    if (isBulk && !v.company.trim()) e.company = "Please enter your organisation name";
    if (!v.product_category) e.product_category = "Please choose a product or service";
    const q = parseInt(v.quantity, 10);
    if (!q || q < 1) e.quantity = "Please enter a quantity of at least 1";
    if (v.design_requirements.trim().length < 3) e.design_requirements = "Please describe your design requirements";
    if (!isBulk && v.message.trim().length < 3) e.message = "Please add a short message";
    if (!v.preferred_contact) e.preferred_contact = "Please choose a preferred contact method";
    if (!v.consent) e.consent = "Please agree to the privacy notice so we can respond to your enquiry";
    return e;
  };

  const focusFirstError = (errs) => {
    const first = Object.keys(errs)[0];
    if (!first || !formRef.current) return;
    const el = formRef.current.querySelector(`[name="${first}"], #${first}`);
    if (el && el.focus) el.focus({ preventScroll: false });
  };

  const onFile = (e) => {
    const f = e.target.files && e.target.files[0];
    if (!f) return;
    const ext = `.${f.name.split(".").pop().toLowerCase()}`;
    if (!ALLOWED.includes(ext)) { setErrors((p) => ({ ...p, reference_file: "Please upload a JPG, PNG, WEBP, PDF, AI or EPS file" })); e.target.value = ""; return; }
    if (f.size > MAX_FILE) { setErrors((p) => ({ ...p, reference_file: "File is too large — maximum 8 MB. You can also send it via WhatsApp." })); e.target.value = ""; return; }
    setErrors((p) => ({ ...p, reference_file: undefined }));
    setFile(f);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) {
      setErrors(errs);
      toast.error("Please check the highlighted fields.");
      focusFirstError(errs);
      return;
    }
    setStatus("submitting");
    const fd = new FormData();
    fd.append("enquiry_type", variant);
    Object.entries(v).forEach(([k, val]) => {
      if (k === "consent") fd.append(k, val ? "true" : "false");
      else if (val !== "" && val !== null && val !== undefined) fd.append(k, val);
    });
    fd.append("source_page", sourcePage || window.location.pathname);
    fd.append("form_started_at", String(startedAt.current));
    if (file) fd.append("reference_file", file);
    try {
      const { data } = await axios.post(`${API}/enquiries`, fd, { timeout: 30000 });
      if (!data || !data.success) throw new Error("Unexpected response");
      setResult({ ...data, first_name: v.first_name, method: v.preferred_contact });
      setStatus("success");
      toast.success("Enquiry received — thank you!");
      track("generate_lead", { form: variant, category: v.product_category });
      track("contact_form_submit", { form: variant, category: v.product_category });
      track(isBulk || data.is_bulk ? "bulk_order_enquiry" : "quote_request", { category: v.product_category });
      if (CATEGORY_EVENTS[v.product_category]) track(CATEGORY_EVENTS[v.product_category], { form: variant });
      setTimeout(() => formRef.current && formRef.current.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
    } catch (err) {
      setStatus("idle");
      const res = err.response;
      if (res && res.status === 422 && res.data && res.data.errors) {
        setErrors(res.data.errors);
        focusFirstError(res.data.errors);
        toast.error(res.data.detail || "Please check the highlighted fields.");
      } else if (res && res.data && res.data.detail) {
        toast.error(typeof res.data.detail === "string" ? res.data.detail : "We couldn't send your enquiry.");
      } else {
        toast.error(`We couldn't send your enquiry. Please try again or WhatsApp ${SITE.phoneDisplay}.`);
      }
    }
  };

  const reset = () => {
    setV(initial); setFile(null); setErrors({}); setResult(null); setStatus("idle"); startedAt.current = Date.now();
  };

  if (status === "success" && result) {
    const methodLabel = { whatsapp: "WhatsApp", phone: "phone", email: "email" }[result.method] || "your preferred method";
    return (
      <div ref={formRef} className={cn("kl-form-card scroll-mt-28", className)} data-testid="quote-form-success" role="status" aria-live="polite">
        <CheckCircle2 className="h-10 w-10 text-[#161616]" aria-hidden="true" />
        <h3 className="kl-h3 mt-5">Thank you, {result.first_name}. Your enquiry has been received.</h3>
        <p className="mt-3 text-black/70">Your reference number is <strong className="font-semibold text-[#161616]" data-testid="quote-form-reference">{result.reference}</strong>.</p>
        <p className="mt-3 text-sm leading-6 text-black/65">KhozaLand will contact you by {methodLabel} to discuss your requirements and send your quotation. <strong className="font-semibold">This is not an order confirmation</strong> — your order is only confirmed once you approve the quotation.</p>
        {result.file_received ? <p className="mt-2 text-sm text-black/65">Your reference file was received securely.</p> : null}
        <div className="mt-6 flex flex-col gap-2 sm:flex-row">
          <a href={whatsappLink("general", `Hello KhozaLand.\n\nI've just submitted a quotation request on your website.\nReference: ${result.reference}`)} target="_blank" rel="noopener noreferrer" onClick={() => track("whatsapp_click", { label: "post_submit" })} className="kl-btn kl-btn-dark" data-testid="quote-success-whatsapp-button"><WhatsAppIcon /> Follow up on WhatsApp</a>
          <button type="button" onClick={reset} className="kl-btn kl-btn-outline" data-testid="quote-form-new-enquiry-button">Send another enquiry</button>
        </div>
      </div>
    );
  }

  const err = (k) => errors[k];
  const aria = (k, hint) => ({ "aria-invalid": Boolean(err(k)) || undefined, "aria-describedby": err(k) ? `${k}-error` : hint ? `${k}-hint` : undefined });
  const inputCls = (k) => cn("kl-input", err(k) && "border-red-600 focus-visible:ring-red-600/20");

  return (
    <form ref={formRef} onSubmit={onSubmit} noValidate className={cn("kl-form-card scroll-mt-28", className)} data-testid={isBulk ? "bulk-quote-form" : "quote-form"} aria-label={isBulk ? "Bulk order quotation form" : "Quotation request form"}>
      {heading}
      <div className="grid gap-5 sm:grid-cols-2">
        <Field id="first_name" label={isBulk ? "Contact first name" : "First name"} required error={err("first_name")}>
          <Input id="first_name" name="first_name" autoComplete="given-name" value={v.first_name} onChange={set("first_name")} className={inputCls("first_name")} {...aria("first_name")} data-testid="quote-form-first-name-input" />
        </Field>
        <Field id="surname" label="Surname" required error={err("surname")}>
          <Input id="surname" name="surname" autoComplete="family-name" value={v.surname} onChange={set("surname")} className={inputCls("surname")} {...aria("surname")} data-testid="quote-form-surname-input" />
        </Field>
        <Field id="email" label="Email" required error={err("email")}>
          <Input id="email" name="email" type="email" inputMode="email" autoComplete="email" value={v.email} onChange={set("email")} className={inputCls("email")} {...aria("email")} data-testid="quote-form-email-input" />
        </Field>
        <Field id="phone" label="Phone / WhatsApp" required error={err("phone")}>
          <Input id="phone" name="phone" type="tel" inputMode="tel" autoComplete="tel" placeholder="e.g. 073 000 0000" value={v.phone} onChange={set("phone")} className={inputCls("phone")} {...aria("phone")} data-testid="quote-form-phone-input" />
        </Field>
        <Field id="company" label={isBulk ? "Organisation" : "Company name, if applicable"} required={isBulk} error={err("company")} className="sm:col-span-2" hint={isBulk ? "Business, school, church, burial society, NGO or event" : undefined}>
          <Input id="company" name="company" autoComplete="organization" value={v.company} onChange={set("company")} className={inputCls("company")} {...aria("company", isBulk)} data-testid="quote-form-company-input" />
        </Field>

        <Field id="product_category" label={isBulk ? "Product type" : "Product or service required"} required error={err("product_category")}>
          <Select value={v.product_category} onValueChange={set("product_category")} name="product_category">
            <SelectTrigger id="product_category" className={cn(inputCls("product_category"), "text-left")} {...aria("product_category")} data-testid="quote-form-category-select">
              <SelectValue placeholder="Choose a category" />
            </SelectTrigger>
            <SelectContent className="bg-white">
              {CATEGORIES.map((c) => (
                <SelectItem key={c.value} value={c.value} data-testid={`quote-form-category-option-${c.value}`}>{c.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
        <Field id="product" label="Specific product" error={err("product")} hint="e.g. golf shirts, mugs, Ingcawe blanket">
          <Input id="product" name="product" value={v.product} onChange={set("product")} className={inputCls("product")} {...aria("product", true)} data-testid="quote-form-product-input" />
        </Field>
        <Field id="quantity" label="Quantity" required error={err("quantity")} hint={isBulk ? "Discounts may be available for 10+ items, depending on the product" : undefined}>
          <Input id="quantity" name="quantity" type="number" min="1" inputMode="numeric" value={v.quantity} onChange={set("quantity")} className={inputCls("quantity")} {...aria("quantity", isBulk)} data-testid="quote-form-quantity-input" />
        </Field>
        <Field id="required_date" label="Required date" error={err("required_date")} hint="Urgent orders are not accepted — please allow time for production">
          <Input id="required_date" name="required_date" type="date" min={today} value={v.required_date} onChange={set("required_date")} className={inputCls("required_date")} {...aria("required_date", true)} data-testid="quote-form-date-input" />
        </Field>

        {showApparel && (
          <>
            <Field id="sizes" label="Sizes" error={err("sizes")} hint="e.g. 5 × M, 3 × L, 2 × XL">
              <Input id="sizes" name="sizes" value={v.sizes} onChange={set("sizes")} className={inputCls("sizes")} {...aria("sizes", true)} data-testid="quote-form-sizes-input" />
            </Field>
            <Field id="colours" label="Garment colours" error={err("colours")}>
              <Input id="colours" name="colours" value={v.colours} onChange={set("colours")} className={inputCls("colours")} {...aria("colours")} data-testid="quote-form-colours-input" />
            </Field>
          </>
        )}

        <Field id="design_requirements" label="Design requirements" required error={err("design_requirements")} className="sm:col-span-2" hint="Logo, names, wording, colours, print position (front / back)…">
          <Textarea id="design_requirements" name="design_requirements" rows={4} value={v.design_requirements} onChange={set("design_requirements")} className={cn(inputCls("design_requirements"), "min-h-[110px]")} {...aria("design_requirements", true)} data-testid="quote-form-design-textarea" />
        </Field>
        <Field id="message" label={isBulk ? "Additional notes" : "Message"} required={!isBulk} error={err("message")} className="sm:col-span-2">
          <Textarea id="message" name="message" rows={3} value={v.message} onChange={set("message")} className={cn(inputCls("message"), "min-h-[90px]")} {...aria("message")} data-testid="quote-form-message-textarea" />
        </Field>
        <Field id="delivery_location" label="Delivery location" error={err("delivery_location")} hint="Suburb / town">
          <Input id="delivery_location" name="delivery_location" value={v.delivery_location} onChange={set("delivery_location")} className={inputCls("delivery_location")} {...aria("delivery_location", true)} data-testid="quote-form-delivery-input" />
        </Field>
        <Field id="budget" label={isBulk ? "Estimated budget" : "Approximate budget"} error={err("budget")} hint="If known, e.g. R3,000">
          <Input id="budget" name="budget" value={v.budget} onChange={set("budget")} className={inputCls("budget")} {...aria("budget", true)} data-testid="quote-form-budget-input" />
        </Field>

        <div className="space-y-2 sm:col-span-2">
          <p className="text-sm font-semibold text-[#161616]">Reference design upload <span className="font-normal text-black/45">(optional)</span></p>
          <input ref={fileRef} id="reference_file" type="file" accept={ALLOWED.join(",")} onChange={onFile} className="sr-only" data-testid="quote-form-file-upload-input" aria-describedby="reference_file-hint" />
          {file ? (
            <div className="flex items-center justify-between gap-3 rounded-[12px] border border-black/15 bg-[#F7F7F7] px-4 py-3" data-testid="quote-form-file-selected">
              <span className="flex min-w-0 items-center gap-2 text-sm"><Paperclip className="h-4 w-4 shrink-0" aria-hidden="true" /><span className="truncate">{file.name}</span><span className="shrink-0 text-black/50">({(file.size / 1024 / 1024).toFixed(1)} MB)</span></span>
              <button type="button" onClick={() => { setFile(null); if (fileRef.current) fileRef.current.value = ""; }} className="kl-icon-btn h-9 w-9" aria-label="Remove file" data-testid="quote-form-file-remove-button"><X className="h-4 w-4" /></button>
            </div>
          ) : (
            <label htmlFor="reference_file" className="flex cursor-pointer items-center gap-3 rounded-[12px] border border-dashed border-black/25 bg-white px-4 py-4 text-sm text-black/70 transition-colors hover:border-black/50 hover:bg-[#F7F7F7] focus-within:ring-2" data-testid="quote-form-file-dropzone">
              <FileUp className="h-5 w-5" aria-hidden="true" /> <span><span className="font-semibold text-[#161616]">Choose a file</span> — logo, sketch or example</span>
            </label>
          )}
          <p id="reference_file-hint" className="text-xs text-black/55">JPG, PNG, WEBP, PDF, AI or EPS · max 8 MB. Files are stored privately and only used for your quotation.</p>
          {err("reference_file") ? <p role="alert" className="text-xs font-medium text-red-700" data-testid="quote-form-reference_file-error">{err("reference_file")}</p> : null}
        </div>

        <fieldset className="space-y-3 sm:col-span-2">
          <legend className="text-sm font-semibold text-[#161616]">Preferred contact method <span className="text-black/45" aria-hidden="true">*</span></legend>
          <RadioGroup value={v.preferred_contact} onValueChange={set("preferred_contact")} className="flex flex-wrap gap-2" name="preferred_contact" aria-label="Preferred contact method">
            {[["whatsapp", "WhatsApp"], ["phone", "Phone call"], ["email", "Email"]].map(([val, label]) => (
              <Label key={val} htmlFor={`contact-${val}`} className={cn("flex h-11 cursor-pointer items-center gap-2 rounded-full border px-4 text-sm font-medium transition-colors", v.preferred_contact === val ? "border-[#161616] bg-[#161616] text-white" : "border-black/15 bg-white text-black/75 hover:border-black/40")}>
                <RadioGroupItem id={`contact-${val}`} value={val} className={cn("border-current", v.preferred_contact === val && "text-white")} data-testid={`quote-form-contact-${val}-radio`} />
                {label}
              </Label>
            ))}
          </RadioGroup>
          {err("preferred_contact") ? <p role="alert" className="text-xs font-medium text-red-700">{err("preferred_contact")}</p> : null}
        </fieldset>

        {/* Honeypot - hidden from people, visible to bots */}
        <div className="absolute -left-[9999px] h-0 w-0 overflow-hidden" aria-hidden="true">
          <label htmlFor="website">Website</label>
          <input id="website" name="website" tabIndex={-1} autoComplete="off" value={v.website} onChange={set("website")} />
        </div>

        <div className="space-y-2 sm:col-span-2">
          <div className="flex items-start gap-3">
            <Checkbox id="consent" name="consent" checked={v.consent} onCheckedChange={(c) => set("consent")(Boolean(c))} className="mt-0.5 h-5 w-5 rounded-[6px]" aria-invalid={Boolean(err("consent")) || undefined} data-testid="quote-form-consent-checkbox" />
            <Label htmlFor="consent" className="text-sm font-normal leading-6 text-black/70">
              I agree that KhozaLand may use my details to respond to this enquiry and prepare a quotation, as described in the <Link to="/privacy-policy" className="underline underline-offset-4" target="_blank">Privacy Policy</Link>.
            </Label>
          </div>
          {err("consent") ? <p role="alert" className="text-xs font-medium text-red-700" data-testid="quote-form-consent-error">{err("consent")}</p> : null}
        </div>
      </div>

      <div className="mt-7 flex flex-col gap-3 sm:flex-row sm:items-center">
        <button type="submit" disabled={status === "submitting"} className="kl-btn kl-btn-dark h-12 px-7 disabled:opacity-70" data-testid={isBulk ? "bulk-quote-submit-button" : "quote-form-submit-button"}>
          {status === "submitting" ? (<><Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> Sending…</>) : isBulk ? "Request a Bulk Quote" : "Send Quote Request"}
        </button>
        <p className="text-xs text-black/55">Prefer chat? <a href={whatsappLink(isBulk ? "bulk" : "general")} target="_blank" rel="noopener noreferrer" onClick={() => track("whatsapp_click", { label: "form_alt" })} data-testid="quote-form-whatsapp-alt-link" className="font-semibold text-[#161616] underline underline-offset-4">WhatsApp {SITE.phoneDisplay}</a></p>
      </div>
    </form>
  );
}
